import { useState, useEffect, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation, useManualQuery } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Banner from '@zenbusiness/zui/Banner';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import {
  classes,
  VGSInputCSS,
  VGSInputStyled,
  StatusIconStyled,
  LabelStyled
} from '#client/components/VGSInput';
import {
  RESET_PIN_MAX_ATTEMPTS,
  PIN_ACTION,
  PIN_ACTION_RESULT,
  DISABLED_PIN_CARD_BANNER
} from '#client/constants/cards';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '#client/constants/contacts';
import { PIN_NOT_MATCH_MESSAGE } from '#client/constants/messages';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCardsContext from '#client/hooks/useCardsContext';
import useConst from '#client/hooks/useConst';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import useVGSCollectUnit from '#client/hooks/useVGSCollectUnit';
import { LOG_PIN_ACTIVITY_MUTATION } from '#client/mutations';
import { GET_CARD_INFO } from '#client/queries';
import { isAllFieldsValid, isFieldValid, getErrorTitle, fieldHasErrors } from '#client/utils/vgs';

import { DialogContentStyled, Wrapper } from './ResetPinForm.styles';

import {
  OLD_PIN_FIELD_NAME,
  NEW_PIN_FIELD_NAME,
  NEW_PIN_CONFIRM_FIELD_NAME,
  PIN_NOT_MATCH_VGS_MESSAGE,
  MAP_VGS_RESET_PIN_ERRORS_TO_ZEN_MESSAGE
} from '../constants/pin';

const validIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/check-circle-green.svg"
  />
);

const CURRENT_PIN_IS_INVALID = 'Incorrect pin entered.';

export default function PinInputForm({ handleClose }) {
  const [form, setForm] = useState(null);
  const [formIsValid, setFormIsValid] = useState(false);
  const [newPinsAreValid, setNewPinsAreValid] = useState(false);
  const [pinIsError, setPinIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotEqualMessage, setShowNotEqualMessage] = useState(false);
  const [oldPinMessage, setOldPinMessage] = useState('');
  const { getCustomerToken } = useCustomerToken({
    type: TOKEN_TYPES.CARDS_SENSITIVE_WRITE
  });

  const { getCustomerToken: getCardCustomerToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });

  const { physicalCard } = useCardsContext();
  const [getCardInfo] = useManualQuery(GET_CARD_INFO, {
    noThrow: true
  });
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [cardInfo, setCardInfo] = useState(null);
  const { createCommonErrorToast } = useToastContext();
  const confirmButtonId = useEnterSubmit();
  const { createSuccessToast, createErrorToast, clearAllToast } = useToastContext();
  const { vgsCollect } = useVGSCollectUnit();
  const captureException = useCaptureException();

  const newPinError = showNotEqualMessage || pinIsError;
  const disableResetPin = useMemo(
    () => cardInfo?.dailyPinResetAttempts >= RESET_PIN_MAX_ATTEMPTS,
    [cardInfo]
  );
  const [logPinActivity] = useMutation(LOG_PIN_ACTIVITY_MUTATION, {
    noThrow: true
  });
  const idempotencyKey = useConst(uuidv4);
  const { unitAccountId } = useUnitAccountId();

  const initForm = (vgsCollectForm) => {
    const clearToasts = () => {
      clearAllToast();
    };
    const clearAllError = () => {
      setOldPinMessage('');
      clearToasts();
    };

    const tempForm = vgsCollectForm.init((state) => {
      const isNotEqual = !!(
        state[NEW_PIN_CONFIRM_FIELD_NAME]?.errorMessages?.length === 1 &&
        state[NEW_PIN_CONFIRM_FIELD_NAME]?.errorMessages?.includes(PIN_NOT_MATCH_VGS_MESSAGE)
      );
      setShowNotEqualMessage(isNotEqual);
      setFormIsValid(isAllFieldsValid(state));
      setNewPinsAreValid(
        isFieldValid(NEW_PIN_FIELD_NAME, state) && isFieldValid(NEW_PIN_CONFIRM_FIELD_NAME, state)
      );
      setPinIsError(fieldHasErrors(NEW_PIN_FIELD_NAME, state));
    });

    tempForm
      .field('#cc-old-pin', {
        type: 'password',
        name: OLD_PIN_FIELD_NAME,
        maxLength: 4,
        validations: ['required', '/^([0-9]{4})$/'],
        css: VGSInputCSS,
        classes,
        ...(disableResetPin ? {} : { autoFocus: true }),
        autoComplete: 'off',
        inputMode: 'numeric'
      })
      .replacePattern('/[^\\d]+/g')
      .on('focus', clearAllError);

    tempForm
      .field('#cc-new-pin', {
        type: 'password',
        name: NEW_PIN_FIELD_NAME,
        maxLength: 4,
        validations: ['required', '/^([0-9]{4})$/'],
        css: VGSInputCSS,
        classes,
        autoComplete: 'off',
        inputMode: 'numeric'
      })
      .replacePattern('/[^\\d]+/g')
      .on('focus', clearToasts);

    tempForm
      .field('#cc-new-pin-confirm', {
        type: 'password',
        name: NEW_PIN_CONFIRM_FIELD_NAME,
        maxLength: 4,
        validations: [
          'required',
          '/^([0-9]{4})$/',
          {
            type: 'compareValue',
            params: {
              field: NEW_PIN_FIELD_NAME,
              function: 'match'
            }
          }
        ],
        css: VGSInputCSS,
        classes,
        autoComplete: 'off',
        inputMode: 'numeric'
      })
      .replacePattern('/[^\\d]+/g')
      .on('focus', clearToasts);

    setForm(tempForm);
  };

  const getCardInfoResponse = async () => {
    const customerToken = await getCardCustomerToken();

    if (!customerToken) {
      return;
    }

    const { data, error } = await getCardInfo({
      variables: {
        params: {
          identifier: {
            businessUuid: businessEntityUuid,
            unitAccountId
          },
          id: physicalCard?.id,
          customerToken
        }
      },
      skipCache: true
    });

    if (error || !data) {
      createCommonErrorToast({ error });
      return;
    }
    setCardInfo(data.card);
  };
  useEffect(() => {
    if (physicalCard?.id && cardInfo == null) {
      getCardInfoResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vgsCollect) {
      initForm(vgsCollect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vgsCollect]);

  const handleUpdatePinAttempt = async (pinActionResult) => {
    const { error, data } = await logPinActivity({
      variables: {
        input: {
          bankingCardId: cardInfo?.id,
          pinAction: PIN_ACTION.RESET,
          pinActionResult,
          idempotencyKey
        }
      }
    });

    if (error) {
      createErrorToast({
        title: 'PIN Reset Failed',
        description: 'Something went wrong while resetting your PIN. Please try again later.',
        error
      });
      return;
    }

    if (pinActionResult === PIN_ACTION_RESULT.FAILURE && !data) {
      getCardInfoResponse();
    }
  };

  const handleResponse = (status, data) => {
    const { errors } = data;
    const resetPinErrorTitle = getErrorTitle(errors);
    const errorTitleAndDescription = MAP_VGS_RESET_PIN_ERRORS_TO_ZEN_MESSAGE[resetPinErrorTitle];
    const sentryErrorMessage = new Error(
      `Reset PIN error status: ${status} and data: ${JSON.stringify(data)}`
    );
    switch (status) {
      case 200:
        createSuccessToast({
          title: 'PIN is reset'
        });
        handleUpdatePinAttempt(PIN_ACTION_RESULT.SUCCESS);
        handleClose();
        break;
      default:
        if (errorTitleAndDescription) {
          createErrorToast({
            title: errorTitleAndDescription.title,
            description: errorTitleAndDescription.description,
            error: errors
          });
          setOldPinMessage(CURRENT_PIN_IS_INVALID);
        } else {
          createErrorToast({
            title: 'Incorrect PIN',
            description: 'This PIN is invalid. Please try again.',
            error: errors
          });
        }
        handleUpdatePinAttempt(PIN_ACTION_RESULT.FAILURE);
        captureException(sentryErrorMessage, { level: 'warning' });
        break;
    }
  };

  const trackFormPressed = useButtonPressedCallback({
    elementName: 'confirm_reset_pin_dc',
    elementText: 'Confirm',
    appPageName: 'active_banking_dashboard'
  });

  const handleFormSubmit = async () => {
    trackFormPressed();

    setIsSubmitting(true);
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    form.submit(
      `/cards/${physicalCard?.id}/secure-data/pin`,
      {
        method: 'PUT',
        mapDotToObject: 'true',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${customerToken}`
        }
      },
      (status, data) => {
        handleResponse(status, data);
        setIsSubmitting(false);
      }
    );
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="12px">
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Reset your PIN
                </Heading>
                <Text
                  color="primary"
                  size="lg"
                >
                  {'If you forgot your PIN, reach out to '}
                  <Link
                    href={`tel:${SUPPORT_PHONE}`}
                    size="lg"
                    underline="none"
                    weight="medium"
                  >
                    {SUPPORT_PHONE}
                  </Link>
                  {' or email '}
                  <Link
                    href={`mailto:${SUPPORT_EMAIL}`}
                    size="lg"
                    underline="none"
                    weight="medium"
                  >
                    {`${SUPPORT_EMAIL}`}
                  </Link>
                  .
                </Text>
              </Stack>
              {disableResetPin && (
                <Banner
                  slots={{
                    endIcon: (
                      <IconButton
                        label="Close form modal"
                        onClick={handleClose}
                      >
                        <SharedAsset
                          alt=""
                          src="/icon/micro/close-x.svg"
                        />
                      </IconButton>
                    )
                  }}
                  subtitle={DISABLED_PIN_CARD_BANNER.SUBTITLE}
                  title={DISABLED_PIN_CARD_BANNER.TITLE}
                  variant="alert"
                />
              )}
              <Stack spacing="24px">
                <Stack
                  position="relative"
                  spacing="4px"
                  width="343px"
                >
                  <VGSInputStyled
                    disabled={disableResetPin}
                    id="cc-old-pin"
                    isError={oldPinMessage}
                  >
                    <LabelStyled isError={oldPinMessage}>Current PIN</LabelStyled>
                  </VGSInputStyled>
                  {oldPinMessage && (
                    <Text
                      color="red"
                      size="md"
                    >
                      {oldPinMessage}
                    </Text>
                  )}
                </Stack>
                <Text size="lg">
                  Choose a unique 4-digit PIN to use for ATMs or debit transactions.
                </Text>
                <Stack
                  position="relative"
                  spacing="4px"
                  width="343px"
                >
                  <VGSInputStyled
                    disabled={disableResetPin}
                    id="cc-new-pin"
                    isError={newPinError}
                  >
                    <LabelStyled isError={newPinError}>Create a PIN</LabelStyled>
                  </VGSInputStyled>
                  {newPinsAreValid && <StatusIconStyled>{validIcon}</StatusIconStyled>}
                </Stack>
                <Stack
                  position="relative"
                  spacing="4px"
                  width="343px"
                >
                  <VGSInputStyled
                    disabled={disableResetPin}
                    id="cc-new-pin-confirm"
                  >
                    <LabelStyled>Confirm your PIN</LabelStyled>
                  </VGSInputStyled>
                  {showNotEqualMessage && (
                    <Text
                      color="red"
                      size="md"
                    >
                      {PIN_NOT_MATCH_MESSAGE}
                    </Text>
                  )}
                  {newPinsAreValid && <StatusIconStyled>{validIcon}</StatusIconStyled>}
                </Stack>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={!formIsValid || isSubmitting || disableResetPin}
          fullWidth
          id={confirmButtonId}
          loading={isSubmitting}
          onClick={handleFormSubmit}
          size="large"
          variant="contained"
        >
          Confirm
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

PinInputForm.propTypes = {
  handleClose: PropTypes.func.isRequired
};
